import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { PROJECT_CLASSIFICATION_TYPES } from './consts';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import isProjectTemplate from './helpers/isProjectTemplate';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useLocalStateData from './hooks/useLocalStateData';
import {
  EntitlementsResponse,
  EntitlementsUser,
  EntitlementsProject,
} from './projectEntitlementTypes';

type ProjectEntitlementsContextValue = {
  loading: boolean;
  error: false | unknown;
  retry(): void;
  currentProject?: EntitlementsProject;
  projects?: EntitlementsProject[];
  user?: EntitlementsUser;
  setUser(user: EntitlementsUser): void;
  setCurrentProject(currentProject: EntitlementsProject): void;
  addProject(project: EntitlementsProject): void;
  updateProject(project: EntitlementsProject): void;
  removeProjectById(id: string): void;
};

/**
 * Context for project entitlements.
 */
export const ProjectEntitlementsContext =
  getOrCreateGlobalContext<ProjectEntitlementsContextValue>(
    CONTEXT_NAMES.ProjectEntitlementsContext
  );

export const useProjectEntitlements = () =>
  useContextWithValidation(ProjectEntitlementsContext, {
    hook: 'useProjectEntitlements',
    provider: 'ProjectEntitlementsProvider',
  });

/**
 * Hook for getting the current project the user work in.
 * This hook returns the current project, as returned from [project-entitlements](https://platform.stoplight.autodesk.com/docs/hq-api-server/swagger.yaml/paths/~1project-entitlements/get)
 request.
 * Return undefined if project entitlements are missing.
 * @returns {undefined|EntitlementsProject} The project entitlements or undefined
 * @example
 * import { useCurrentProject } from '@adsk/alloy-provider';
 * ...
 * const currentProject = useCurrentProject();
 */
export const useCurrentProject = () => {
  const { currentProject } = useProjectEntitlements();
  return currentProject;
};

/**
 * Hook for getting the current logged in user.
 * This hook returns the current user, as returned from [project-entitlements](https://platform.stoplight.autodesk.com/docs/hq-api-server/swagger.yaml/paths/~1project-entitlements/get)
 request.
 * @returns {{ user: EntitlementsUser, setUser: (user: EntitlementsUser) => void }} An object of shape {user, setUser} - containing the current user and setUser function to update the current user.
 * @example
 * import { useUser } from '@adsk/alloy-provider';
 * ...
 * const { user, setUser } = useUser();
 */
export const useUser = () => {
  const { user, setUser } = useProjectEntitlements();
  return useMemo(() => (user ? { user, setUser } : {}), [user, setUser]);
};

/**
 * This hook returns whether or not the current project is a template.
 * @returns {boolean} Whether the current project entitlements is a template or not.
 * @example
 * import { useCurrentProjectIsTemplate } from '@adsk/alloy-provider';
 * ...
 * const isTemplate = useCurrentProjectIsTemplate();
 */
export const useCurrentProjectIsTemplate = () => {
  const currentProject = useCurrentProject();
  return currentProject ? isProjectTemplate(currentProject) : false;
};

const fields =
  'accountId,name,accountName,accountImageUrl,defaultUrl,classification,imageUrl,region,products,platform';
const classificationFilter = `filter[classification]=${Object.values(
  PROJECT_CLASSIFICATION_TYPES
).join(',')}`;

const getCurrentProjectIdPart = (projectId: string | undefined) =>
  projectId ? `currentProjectId=${projectId}&` : '';

export const ProjectEntitlementsProvider: ComponentType = ({ children }) => {
  const projectId = useCurrentProjectId();

  const [user, setUser] = useState<EntitlementsUser | undefined>(undefined);
  const [currentProject, setCurrentProject] = useState<
    EntitlementsProject | undefined
  >(undefined);

  const url = `${getAdminApiUrl()}/v1/project-entitlements?${getCurrentProjectIdPart(
    projectId
  )}fields=${fields}&${classificationFilter}&limit=200`;

  const {
    data: dataFromBE,
    loading: loadingData,
    error,
    retry,
  } = useGetAllPaginatedData<EntitlementsResponse>({
    url,
  });

  useEffect(() => {
    if (error && error.status !== 401) {
      // 401 errors are pretty common and expected, here we want to mostly log unexpected ones
      console.error('Error calling project entitlements', error);
    }
  }, [error]);

  const firstPage = dataFromBE[0];
  useEffect(() => {
    if (firstPage) {
      if (firstPage.currentProject) {
        setCurrentProject(firstPage.currentProject);
      }
      if (firstPage.user) {
        setUser(firstPage.user);
      }
    }
  }, [firstPage]);

  const projectsFromBE = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy<EntitlementsProject>(
        dataFromBE.flatMap((page) => page.results),
        (item) => item.id
      )
    );
  }, [dataFromBE]);

  const {
    data: projects,
    addData: addProjects,
    updateEntity: updateProject,
    removeEntityById: removeProjectById,
  } = useLocalStateData<EntitlementsProject>(projectsFromBE);

  const addProject = useCallback(
    (project) => {
      addProjects([project]);
    },
    [addProjects]
  );

  const loading =
    loadingData || Boolean(projectsFromBE.length && !projects.length);

  const value = useMemo(
    () => ({
      loading,
      error,
      retry,
      projects,
      user,
      setUser,
      currentProject,
      setCurrentProject,
      addProject,
      updateProject,
      removeProjectById,
    }),
    [
      projects,
      user,
      currentProject,
      loading,
      error,
      retry,
      addProject,
      removeProjectById,
      updateProject,
    ]
  );

  return (
    <ProjectEntitlementsContext.Provider value={value}>
      {children}
    </ProjectEntitlementsContext.Provider>
  );
};

ProjectEntitlementsProvider.displayName = 'ProjectEntitlementsProvider';

ProjectEntitlementsProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
};
